import React, { useCallback, useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { parseISO } from 'date-fns';

import flatten from 'lodash/flatten';
import isFunction from 'lodash/isFunction';
import uniq from 'lodash/uniq'

import formatRelativeDate from '../../../utils/format_relative_date';
import { truncateText } from '../../../utils/text_utils';

import ClipboardLabel from '../../common/ClipboardLabel/ClipboardLabel';
import CodeFormatLabel from '../../common/Labels/CodeFormatLabel';
import ContentTags from '../../ContentTags/ContentTags';
import DataTable from '../../common/DataTable/DataTable';
import Label from '../../common/Labels/Label';
import LinkLabel from '../../common/Labels/LinkLabel';
import { preserveWorkspaceURL } from '../../../utils/url_utils';

const getAllModelTags = (modelGrouping = []) => {
  const allTags = flatten(modelGrouping.map(({ modelTags = [] }) => modelTags));
  const strTags = allTags.map(({ tag }) => tag?.tag);
  return uniq(strTags);
}

const transformModelsToTableRows = (modelGroupings = {}) => {
  const modelKeys = Object.keys(modelGroupings);
  return modelKeys.map((modelID) => {
    const latestModelVersion = modelGroupings[modelID][0]; // TODO - Assumes the list is presorted - check if this is valid
    return ({
      modelID,
      latestModelVersion,
      sha: latestModelVersion.sha,
      modelVersion: latestModelVersion.modelVersion,
      createdAt: latestModelVersion?.createdAt,
      updatedAt: latestModelVersion?.updatedAt,
      modelTags: getAllModelTags(modelGroupings[modelID]),
    })
  });
}

const RelativeDateCellRenderer = ({ value }) => <Label>{formatRelativeDate(parseISO(value))}</Label>

const LatestVersionCellRenderer = ({ row, value }) => {
  const modelSha = row?.original?.sha

  const truncatedSha = useMemo(() => truncateText(modelSha), [modelSha]);

  return (
    <div className="flex flex-row flex-wrap items-center p-2">
      {
        modelSha !== undefined ? (
          <>
            <ClipboardLabel
              className="p-1"
              iconSize={16}
              labelValue={modelSha}
              displayLabel={truncatedSha}
              LabelComponent={CodeFormatLabel}
              showClipboardIconOnHover
            />
            <span className="mx-1">&middot;</span>
          </>
        ) : null
      }
      {
        value ? (
          <Label>{formatRelativeDate(parseISO(value))}</Label>
        ) : (
          <Label>N/A</Label>
        )
      }
    </div>
  )
}

const ModelCellRenderer = ({ row, value }) => {
  const modelTags = row?.original?.modelTags;
  const modelVersion = row?.original?.modelVersion

  const truncatedName = truncateText(value, 30)

  return (
    <div className="flex flex-col p-2">
      <div className="flex flex-col flex-wrap justify-center mb-2">
        <LinkLabel
          size="2xl"
          to={preserveWorkspaceURL(`/models/${value}/versions/${modelVersion}`)}
          title={value}
          bold
        >
          {truncatedName}
        </LinkLabel>
      </div>
      <ContentTags tags={modelTags} />
    </div>
  )
}

export const ModelDataTable = ({ modelGroupings = {} }) => {
  const history = useHistory();
  const columns = useMemo(() => [
    {
      Header: 'Model',
      accessor: 'modelID',
      Cell: ModelCellRenderer,
    },
    {
      Header: 'Created',
      accessor: 'createdAt',
      Cell: RelativeDateCellRenderer,
      style: { width: '1%' },
    },
    {
      Header: 'Latest Version',
      accessor: 'updatedAt',
      Cell: LatestVersionCellRenderer,
      style: { width: '1%' },
    },
  ], []);
  const data = useMemo(() => transformModelsToTableRows(modelGroupings), [modelGroupings]);
  const defaultColumn = React.useMemo(
    () => ({
      minWidth: 120,
      width: 240,
      maxWidth: 420,
    }),
    []
  )

  const pushHistory = history?.push;
  const onItemClick = useCallback((row) => {
    const modelID = row?.original?.modelID
    const modelVersion = row?.original?.modelVersion;

    if (modelID && modelVersion && isFunction(pushHistory)) {
      pushHistory(preserveWorkspaceURL(`/models/${modelID}/versions/${modelVersion}`)(history.location))
    }
  }, [pushHistory, history.location]);

  return (
    <DataTable
      id="model-data-table"
      columns={columns}
      data={data}
      defaultColumn={defaultColumn}
      onItemClick={onItemClick}
    />
  )
};

export default ModelDataTable;
